// Theme cookie helpers — partage le thème entre sous-domaines de neuroforge.link
// via un cookie Domain=.neuroforge.link, complété par localStorage côté same-origin.

export type Theme = 'light' | 'dark'

export const THEME_COOKIE = 'neuroforge-theme'
export const THEME_STORAGE_KEY = 'neuroforge-theme'
// Legacy keys (Forge + Hub historique) — lues en migration uniquement
export const LEGACY_STORAGE_KEYS = ['laforge-theme', 'neuroforge-hub-theme'] as const

/**
 * Calcule le domaine parent pour partager le cookie entre apps :
 * hub.neuroforge.link / academy.neuroforge.link / prompts.neuroforge.link / forge.neuroforge.link
 * → cookie Domain=.neuroforge.link
 *
 * En dev (localhost / IP / 1 seul label), on retourne null et on n'attache pas de Domain.
 */
export function parentCookieDomain(
  host: string = typeof location !== 'undefined' ? location.hostname : '',
): string | null {
  if (!host) return null
  // localhost ou IP — pas de domaine parent partageable
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null
  const parts = host.split('.').filter(Boolean)
  if (parts.length < 2) return null
  // Toujours retourner le suffixe à 2 labels : .neuroforge.link
  return '.' + parts.slice(-2).join('.')
}

function isTheme(v: unknown): v is Theme {
  return v === 'light' || v === 'dark'
}

export function readThemeCookie(): Theme | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + THEME_COOKIE + '=([^;]+)'))
  if (!match) return null
  const v = decodeURIComponent(match[1])
  return isTheme(v) ? v : null
}

export interface WriteCookieOptions {
  maxAgeSeconds?: number
  domain?: string | null
  secure?: boolean
  sameSite?: 'Lax' | 'Strict' | 'None'
}

export function writeThemeCookie(theme: Theme, opts: WriteCookieOptions = {}): void {
  if (typeof document === 'undefined') return
  const maxAge = opts.maxAgeSeconds ?? 60 * 60 * 24 * 365 // 1 an
  const domain = opts.domain === undefined ? parentCookieDomain() : opts.domain
  const secure = opts.secure ?? (typeof location !== 'undefined' && location.protocol === 'https:')
  const sameSite = opts.sameSite ?? 'Lax'
  let cookie = `${THEME_COOKIE}=${encodeURIComponent(theme)}; Path=/; Max-Age=${maxAge}; SameSite=${sameSite}`
  if (domain) cookie += `; Domain=${domain}`
  if (secure) cookie += '; Secure'
  document.cookie = cookie
}

export function readThemeStorage(): Theme | null {
  if (typeof localStorage === 'undefined') return null
  const v = localStorage.getItem(THEME_STORAGE_KEY)
  if (isTheme(v)) return v
  // Migration legacy → ne casse pas l'expérience des utilisateurs existants
  for (const k of LEGACY_STORAGE_KEYS) {
    const legacy = localStorage.getItem(k)
    if (isTheme(legacy)) return legacy
  }
  return null
}

export function writeThemeStorage(theme: Theme): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(THEME_STORAGE_KEY, theme)
  // Garde laforge-theme à jour pour compat Forge legacy (ancien toggle)
  localStorage.setItem('laforge-theme', theme)
}

export function readOsPreference(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Résout le thème initial avec priorité : cookie → localStorage → OS.
 */
export function resolveInitialTheme(): Theme {
  return readThemeCookie() ?? readThemeStorage() ?? readOsPreference()
}

/**
 * Applique le thème sur le document : <html data-theme="..."> + body.light-theme legacy.
 * Source unique de vérité pour le DOM — appelée par ThemeProvider et le snippet anti-FOUC.
 */
export function applyThemeToDocument(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
  document.body?.classList.toggle('light-theme', theme === 'light')
}

export const THEME_BROADCAST_CHANNEL = 'neuroforge-theme'
